import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../http-common";
import _ from "lodash";
import moment from "moment";

export const STALE_TIME = 40_000;

export const processResult = (qc, r) => {
	const data = r?.data?.data;
	const metadata = r?.data?.metadata;
	const touched = metadata?.touched;
	if (touched && Object.keys(touched)) {
		const queries = [];
		Object.keys(touched).forEach(key => {
			touched[key].forEach(id => {
				queries.push([key, id]);
			});
		});
		qc.invalidateQueries(queries);
	}

	if( data && metadata ){
		data._metadata = metadata;
	}
	return data;
};

export function createCrudHooks({ baseKey, basePath, getAllParams, getParams }) {
	const listKey = [baseKey];
	const itemKey = (id) => [baseKey, id];

	const useGetAll = () => {
		const qc = useQueryClient();
		return useQuery({
			queryKey: listKey,
			queryFn: () => api.get(basePath, { params: getAllParams }).then(r => processResult(qc, r)),
			staleTime: STALE_TIME,
		});
	};

	const useGet = (id) => {
		const qc = useQueryClient();
		const navigate = useNavigate();
		return useQuery({
			queryKey: itemKey(id),
			queryFn: () => {
				if (!id) {
					return null;
				}
				return api.get(`${basePath}/${id}`, { params: getParams })
					.then(r => processResult(qc, r))
					.catch((e) => navigate(basePath));
			},
			// initialData: () => {
			// 	const items = qc.getQueryData(listKey);
			// 	const item = items?.find((item) => item.id === id);
			// 	return item;
			// },
			staleTime: STALE_TIME,
		});
	};

	const cleanse = (data) => {
		if (_.isArray(data)) {
			return _.map(data, obj => cleanse(obj));
		}
		else {
			data = _.mapValues(data, val => val instanceof moment ? val.valueOf() : val);
			return _.omitBy(data, _.isObjectLike);
		}
	};

	const useSave = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: (data) => {
				data = cleanse(data);
				if (data?.id) {
					return api.put(`${basePath}/${data.id}`, data).then(r => processResult(qc, r));
				}
				else {
					return api.post(basePath, data).then(r => processResult(qc, r));
				}
			},
			onSuccess: (result) => {
				qc.invalidateQueries([listKey, itemKey(result.id)]);
			},
		});
	};

	const useUpdate = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: (data) => {
				data = cleanse(data);
				if (_.isArray(data)) {
					for (var i in data) {
						if (data[i].id?.indexOf('-') === 0) {
							delete data[i].id;
						}
					}
				}
				return api.put(`${basePath}/${data.id}`, data).then(r => processResult(qc, r))
			},
			onSuccess: (result) => {
				qc.setQueryData(itemKey(result.id), result);
				qc.setQueryData(listKey, (old = []) =>
					old.map((item) => (item.id === result.id ? result : item))
				);
			},
		});
	};

	const usePatch = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: (data) => api.patch(`${basePath}`, data).then(r => processResult(qc, r)),
			onSuccess: (result) => {
				qc.invalidateQueries([itemKey(result.id)]);
			},
		});
	};

	const usePatchAll = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: (data) => api.patch(`${basePath}`, data).then(r => processResult(qc, r)),
			onSuccess: (result) => {
				qc.setQueryData(itemKey(result.id), result);
				qc.setQueryData(listKey, (old = []) =>
					old.map((item) => (item.id === result.id ? result : item))
				);
			},
		});
	};

	const useDelete = () => {
		const qc = useQueryClient();
		const location = useLocation();
		const navigate = useNavigate();

		return useMutation({
			mutationFn: (id) => api.delete(`${basePath}/${id}`),
			onSuccess: (_, id) => {
				if (location.pathname.indexOf(`${basePath}/${id}`) >= 0) {
					navigate(basePath);
				}
				qc.invalidateQueries([listKey, itemKey(id)]);
				qc.setQueryData(listKey, (old = []) => old.filter((item) => item.id !== id));
				setTimeout(() => {
					qc.removeQueries({ queryKey: itemKey(id) });
				}, 100);
			},
		});
	};

	const useSearch = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: (term) => api.get(`${basePath}/search/${term}`).then(r => processResult(qc, r)),
		});
	};

	const usePreUpload = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: ({ id, data }) => api.post(`${basePath}/${id}/_preUpload`, data).then(r => processResult(qc, r)),
		});
	};

	const usePreDownload = () => {
		const qc = useQueryClient();
		return useMutation({
			mutationFn: ({ id, data }) => api.post(`${basePath}/${id}/_preDownload`, data).then(r => processResult(qc, r)),
		});
	};

	return { useGetAll, useGet, useSave, useUpdate, usePatch, usePatchAll, useDelete, useSearch, usePreUpload, usePreDownload };
}
